import React, { useState, useEffect } from "react";
import Axios from "axios";
import { API_BASE_URL, GET_PAYMENTS_TYPES, INSERT_ORDERS } from "../../Config";
import PropTypes from "prop-types";
import SpinLoader from "../loader/SpinLoader";
import Select from "react-select";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { Trash2 } from "react-feather";

const CompleteOrder = ({ booking, onClose }) => {
  const MySwal = withReactContent(Swal);
  const quotationId = booking?.Id;
  const [userDataSession, setUserDataSession] = useState(null);
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formattedDate, setFormattedDate] = useState("");
  const [formattedExpectedDate, setFormattedExpectedDate] = useState("");
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [payments, setPayments] = useState([]); 
  const [currentPaymentAmount, setCurrentPaymentAmount] = useState("");
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);

  useEffect(() => {
    const userDataString = sessionStorage.getItem("userData");
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      setUserDataSession(userData);
    }
  }, []);

  useEffect(() => {
    if (data) {
      if (data?.Quotaiondate) {
        const date = new Date(data?.Quotaiondate);
        setFormattedDate(date.toISOString().split("T")[0]);
      }
      if (data?.ExpectedDelivery) {
        const expDate = new Date(data.ExpectedDelivery);
        setFormattedExpectedDate(expDate.toISOString().split("T")[0]);
      }
    }
  }, [data]);

  useEffect(() => {
    if (!quotationId || !userDataSession?.OrgId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const url = `${API_BASE_URL}Billing/getQuotationsById?QuotaionId=${quotationId}`;
        const res = await Axios.get(url);
        if (res.data.Status && res.data.ResultData?.length) {
          const result = res.data.ResultData;
          setData(result[0]);
          setProducts(result);

          if (result[0]?.AdvanceAmount > 0) {
            setPayments([
              {
                PaymentTypeId: 5,
                PaidAmount: parseFloat(result[0].AdvanceAmount),
                CardNo: "",
                MobileNo: "",
              },
            ]);
          }
        }

        // Fetch payment types
        const paymentsRes = await Axios.get(
          `${API_BASE_URL}${GET_PAYMENTS_TYPES}?OrgId=${userDataSession.OrgId}`
        );
        if (paymentsRes.data.Status) {
          setPaymentTypes(paymentsRes.data.ResultData);
        }
      } catch (err) {
        console.error(err);
        MySwal.fire("Error", "Failed to load data", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [quotationId, userDataSession]);

  
  const calculateAmounts = () => {
    let subtotal = 0;
    products.forEach((p) => {
      subtotal += parseFloat(p.UnitPrice || 0) * parseFloat(p.Quantity || 0);
    });
   
    const totalTax = parseFloat(data?.HeaderTotal || 0) - subtotal;
    const finalTotal = parseFloat(data?.HeaderTotal || 0);
    const totalPaid = payments.reduce((sum, p) => sum + p.PaidAmount, 0);
    const remainingAmount = finalTotal - totalPaid;
    return { subtotal, totalTax, finalTotal, totalPaid, remainingAmount };
  };

  const amounts = calculateAmounts();

  const handlePaymentTypeChange = (selectedOption) => {
    setSelectedPaymentType(selectedOption ? selectedOption.value : null);
  };

  const handleAddPayment = () => {
    const amt = parseFloat(currentPaymentAmount);
    if (!isNaN(amt) && amt > 0 && selectedPaymentType) {
      const existingIndex = payments.findIndex(
        (p) => p.PaymentTypeId === selectedPaymentType
      );
      if (existingIndex !== -1) {
        const updated = [...payments];
        updated[existingIndex].PaidAmount += amt;
        setPayments(updated);
      } else {
        setPayments([
          ...payments,
          {
            PaymentTypeId: selectedPaymentType,
            PaidAmount: amt,
            CardNo: "",
            MobileNo: "",
          },
        ]);
      }
      setCurrentPaymentAmount("");
    } else {
      MySwal.fire(
        "Invalid",
        "Enter valid amount and select payment type",
        "warning"
      );
    }
  };

  const handleRemovePayment = (index) => {
    // Prevent removing advance if it's the only one
    if (
      payments.length === 1 &&
      payments[index].PaidAmount === parseFloat(data?.AdvanceAmount || 0)
    ) {
      MySwal.fire(
        "Cannot Remove",
        "Advance payment cannot be removed here.",
        "warning"
      );
      return;
    }
    setPayments(payments.filter((_, i) => i !== index));
  };

  const handleCompleteOrder = async () => {
    if (amounts.remainingAmount > 0) {
      MySwal.fire("Incomplete", "Please pay the remaining amount.", "warning");
      return;
    }

    const payload = {
      orgid: userDataSession.OrgId,
      userid: userDataSession.UserId,
      OrgType: 0,
      BillNo: null,
      Order: {
        StoreId: userDataSession.StoreId || 5,
        PosId: 0,
        OrderType: 1,
        Total: amounts.finalTotal,
        Discount: 0,
        SGST: 0, 
        CGST: 0,
        IGST: 0,
        Status: 1,
        IsActive: 1,
        Orderdate: new Date().toISOString(),
        DiscountId: 0,
        DiningId: 0,
        ReturnFromOrdId: 0,
        CustomerId: data?.CustomerId || 0,
        PriceListId: 0,
        TransportMode: "NULL",
        TransportvehicleNumber: "NULL",
        AdditionalCharges: 0,
        AdditionalRoundOffAmount: 0,
        IsAcknowledge: 0,
        TransportAmount: 0,
        InvoiceNo: "NULL",
        EmpId: userDataSession?.UserId,
        ExpectedDelivery: data?.ExpectedDelivery || new Date().toISOString(),
      },
      OrderDetails: products.map((prod) => ({
        ProductId: prod.ProductId,
        Quantity: prod.Quantity,
        UnitPrice: parseFloat(prod.UnitPrice || 0),
        Discount: 0,
        SGST: 0, 
        CGST: 0,
        IGST: 0,
        Total: parseFloat(prod.UnitPrice || 0) * parseFloat(prod.Quantity || 0),
        Status: 1,
        RefundQuantity: 0,
        IsActive: 1,
        VendorId: 0,
        IsComplement: 0,
        CGSTrate: 0,
        SGSTrate: 0,
        IGSTrate: 0,
        NoOfBags: 0,
        Comments: data?.Notes || "",
        ItemWeight: 1,
        ItemPackingWeight: 0,
        BilledBarCode: prod.BarCode || "NULL",
      })),
      OrderAdditionalDetails: products.map((prod) => ({
        ProductId: prod.ProductId,
        MakingCharges: 0,
        OtherCharges: 0,
        MetalRateId: 0,
      })),
      OrderPayments: payments.map((p) => ({
        PaymentTypeId: p.PaymentTypeId,
        PaidAmount: p.PaidAmount,
        CardNo: p.CardNo || "",
        MobileNo: p.MobileNo || "",
      })),
      StockHistory: products.map((prod) => ({
        ProductId: prod.ProductId,
        StoreId: userDataSession.StoreId || 5,
        StockEffectTypeId: 2,
        StockAdjusted: parseFloat(prod.Quantity || 0),
        TransactionId: 0,
        TransfarStockId: 0,
        StockAdjustId: 0,
        IsActive: 1,
        SupplierId: 0,
      })),
      StoreProducts: products.map((prod) => ({
        ProductId: prod.ProductId,
        StoreId: userDataSession.StoreId || 5,
        StockAdjusted: -parseFloat(prod.Quantity || 0),
      })),
    };

    try {
      setOrderLoading(true);
      const response = await Axios.post(
        `${API_BASE_URL}${INSERT_ORDERS}`,
        payload
      );
      const respData = response.data;
      if (respData && respData[0]?.OrderId >= 1) {
        
        await Axios.post(`${API_BASE_URL}Billing/UpdateQuotationStatus`, {
          Id: quotationId,
          Status: 2, 
          UpdatedBy: userDataSession?.UserId,
          OrgId:userDataSession?.OrgId
        });

        MySwal.fire({
          icon: "success",
          title: "Order Completed!",
          text: `Order #${respData[0].OrderId} created from booking ${data?.QuotaionNo}.`,
        }).then(() => {
          onClose();
        });
      } else {
        throw new Error("Invalid response");
      }
    } catch (err) {
      console.error("Complete Order Error:", err);
      MySwal.fire(
        "Error",
        err.response?.data?.[0]?.ResultMessage || "Failed to complete order",
        "error"
      );
    } finally {
      setOrderLoading(false);
    }
  };



  return (
    <>
      {loading && <SpinLoader />}
      <div
        className="offcanvas offcanvas-end"
        id="offcanvasCompleteOrder"
        // tabIndex="-1"
        style={{ maxWidth: "40%" }}
      >
        <div className="offcanvas-header">
          <h4>Complete Order ({data?.QuotaionNo})</h4>
          <button
            className="btn-close"
            data-bs-dismiss="offcanvas"
            onClick={onClose}
          ></button>
        </div>
        <hr />

        <div className="offcanvas-body">
          {/* Customer Info (Read-only) */}
          <div className="row g-3 mb-3">
            <div className="col-6">
              <label className="form-label">Customer Name</label>
              <input
                type="text"
                className="form-control"
                value={data?.CustomerName || "Walk-in"}
                readOnly
              />
            </div>
            <div className="col-6">
              <label className="form-label">Mobile No</label>
              <input
                type="text"
                className="form-control"
                value={data?.CustomerMobile || "—"}
                readOnly
              />
            </div>
            <div className="col-6">
              <label className="form-label">Date</label>
              <input
                type="text"
                className="form-control"
                value={formattedDate}
                readOnly
              />
            </div>
            <div className="col-6">
              <label className="form-label">Expected Delivery</label>
              <input
                type="text"
                className="form-control"
                value={formattedExpectedDate}
                readOnly
              />
            </div>
          </div>

          {/* Non-Editable Products Table */}
          <div className="table-responsive" style={{ maxHeight: "200px" }}>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-center">Qty</th>
                  <th className="text-end">Price</th>
                </tr>
              </thead>
              <tbody>
                {products.map((item, i) => (
                  <tr key={i}>
                    <td className="small">{item.ProductName}</td>
                    <td className="text-center small">{item.Quantity}</td>
                    <td className="text-end small">
                      ₹{parseFloat(item.UnitPrice || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <hr />

          {/* Payment Section (Like POS) */}
          <div className="payment-section">
            <h6>Payments</h6>
            <div className="row align-items-end mb-3">
              <div className="col-sm-5">
                <label>Payment Type</label>
                <Select
                  className="select"
                  onChange={handlePaymentTypeChange}
                  options={paymentTypes.map((pt) => ({
                    value: pt.Id,
                    label: pt.Name,
                  }))}
                  placeholder="Choose type"
                />
              </div>
              <div className="col-sm-5">
                <label>Payment Amount</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  placeholder="Enter payment amount"
                  value={currentPaymentAmount}
                  onChange={(e) => setCurrentPaymentAmount(e.target.value)}
                />
              </div>
              <div className="col-sm-2">
                <label>&nbsp;</label>
                <button
                  className="btn btn-primary w-100"
                  onClick={handleAddPayment}
                >
                  Add
                </button>
              </div>
            </div>
            {payments.length > 0 && (
              <div className="payment-list mb-3">
                {payments.map((payment, index) => {
                  const paymentTypeName =
                    paymentTypes.find((pt) => pt.Id === payment.PaymentTypeId)
                      ?.Name || "Unknown";
                  return (
                    <div
                      key={index}
                      className="d-flex justify-content-between align-items-end mb-1 p-1 border"
                    >
                      <span>
                        {paymentTypeName}: ₹{payment.PaidAmount.toFixed(2)}
                      </span>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleRemovePayment(index)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })}
                <hr />
                <div className="d-flex justify-content-end">
                  <strong>Total Paid:</strong>{" "}
                  <strong>₹{amounts.totalPaid.toFixed(2)}</strong>
                </div>
                <div className="d-flex justify-content-end">
                  <strong>Remaining:</strong>{" "}
                  <strong>₹{amounts.remainingAmount.toFixed(2)}</strong>
                </div>
              </div>
            )}
          </div>

          {/* Compact Totals */}
          <div className="order-total mt-3">
            <table className="table table-borderless">
              <tbody>
                <tr>
                  <td>Subtotal</td>
                  <td>₹{amounts.subtotal.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>Tax</td>
                  <td>₹{amounts.totalTax.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td>
                    <strong>₹{amounts.finalTotal.toFixed(2)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="modal-footer-btn mt-3">
            <button
              className="btn btn-cancel btn-sm me-2"
              data-bs-dismiss="offcanvas"
              onClick={onClose}
            >
              Close
            </button>
            <button
              className="btn btn-success btn-sm"
              onClick={handleCompleteOrder}
              disabled={amounts.remainingAmount > 0 || orderLoading}
            >
              {orderLoading ? "Completing..." : "Complete Order"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

CompleteOrder.propTypes = {
  booking: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default CompleteOrder;
